import React from 'react';
import { PercentUpIcon, PercentDownIcon } from '../../../../assets/icon';
import type { ProfitLossReportData, MessageButton } from '../../../../types/chat-message';
import { ReportActionButtons } from './ReportActionButtons';
import { ReportTitle } from './ReportTitle';
import './RevenueReport.scss';

const formatCurrency = (amount?: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount ?? 0) + 'đ';
};

interface MetricIndicatorProps {
  isUp: boolean;
  rate: number;
}

const MetricIndicator: React.FC<MetricIndicatorProps> = ({ isUp, rate }) => (
  <>
    <span className="rr-metric-direction-icon">
      {isUp ? <PercentUpIcon /> : <PercentDownIcon />}
    </span>
    <span className={`rr-metric-change ${isUp ? 'up' : 'down'}`}>
      {Math.abs(rate).toFixed(2)}%
    </span>
  </>
);

interface MetricRowProps {
  label: string;
  value?: number;
  rate?: number;
}

const MetricRow: React.FC<MetricRowProps> = ({ label, value, rate }) => {
  const isUp = typeof rate === 'number' && rate > 0;
  const isDown = typeof rate === 'number' && rate < 0;

  return (
    <div className="rr-metric-row">
      <div className="rr-metric-label-group">
        <span className="rr-metric-label">{label}</span>
        {(isUp || isDown) && <MetricIndicator isUp={isUp} rate={rate!} />}
      </div>
      <span className="rr-metric-value">{formatCurrency(value)}</span>
    </div>
  );
};

const ProfitLossCard: React.FC<{ data: ProfitLossReportData }> = ({ data }) => (
  <div className="rr-card">
    <div className="rr-metrics">
      <MetricRow label="Lợi nhuận" value={data?.totalProfit} rate={data?.totalProfitRate} />
      <MetricRow label="Doanh thu" value={data?.totalRevenue} rate={data?.totalRevenueRate} />
      <MetricRow label="Giá vốn" value={data?.totalCost} rate={data?.totalCostRate} />
      <MetricRow label="Lợi nhuận khác" value={data?.totalProfitOther} rate={data?.totalProfitOtherRate} />
    </div>
  </div>
);

const ProfitLossHeader: React.FC<{ text?: string | null; timeRange?: string }> = ({
  text,
  timeRange,
}) => (
  <div className="rr-header">
    <ReportTitle text={text || `Báo cáo lãi lỗ ${timeRange ?? ''}`.trim()} />
  </div>
);

interface ProfitLossButtonsProps {
  buttons?: MessageButton[];
  isDisabled: boolean;
  onButtonClick?: (name: string, id: number, isHidden?: boolean) => void;
}

const ProfitLossButtons: React.FC<ProfitLossButtonsProps> = ({
  buttons,
  isDisabled,
  onButtonClick,
}) => (
  <ReportActionButtons
    buttons={buttons}
    isDisabled={isDisabled}
    onButtonClick={onButtonClick}
    reportType="Báo cáo lãi lỗ"
    suggestionTitle="Bạn có muốn xem thời gian khác không?"
  />
);

interface ProfitLossReportProps {
  data?: ProfitLossReportData;
  buttons?: MessageButton[];
  onButtonClick?: (name: string, id: number, isHidden?: boolean) => void;
  isLastMessage?: boolean;
  isFromHistory?: boolean;
  text?: string | null;
}

export const ProfitLossReport: React.FC<ProfitLossReportProps> = (props) => {
  const { data, buttons, onButtonClick, isLastMessage, isFromHistory, text } = props;
  const isDisabled = !isLastMessage || !!isFromHistory;

  return (
    <div className="revenue-report profit-loss-report">
      <ProfitLossHeader text={text} timeRange={data?.timeRange} />
      {data && <ProfitLossCard data={data} />}
      <ProfitLossButtons buttons={buttons} isDisabled={isDisabled} onButtonClick={onButtonClick} />
    </div>
  );
};
